<template>
  <div class="note-embed-wrapper my-4">
    <v-card 
      v-if="note && !isExpanded" 
      variant="flat" 
      class="note-embed-card overflow-hidden border border-white-05 bg-black-05" 
      @click.stop="isExpanded = true"
    >
      <div class="d-flex flex-column flex-sm-row">
        <div class="pa-4 flex-grow-1">
          <div class="d-flex align-center mb-2">
            <v-icon icon="mdi-book-open-page-variant" size="x-small" color="primary" class="mr-1"></v-icon>
            <span class="text-overline text-grey-lighten-1 font-weight-bold" style="letter-spacing: 0.1em;">GAMEDEV NOTE</span>
          </div>
          <h3 class="text-h5 font-weight-black mb-2 line-clamp-2" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; letter-spacing: -0.5px;">{{ note.value.title }}</h3>
          <p class="text-body-2 text-grey-lighten-1 line-clamp-2 mb-4" style="font-weight: 400;">{{ snippet }}</p>
          <div class="d-flex align-center">
            <v-btn
              variant="tonal"
              size="small"
              color="primary"
              rounded="pill"
              class="text-none font-weight-bold mr-2"
              append-icon="mdi-chevron-down"
              @click.stop="isExpanded = true"
            >
              Read
            </v-btn>
            <v-btn
              variant="text"
              size="small"
              color="grey-lighten-1"
              rounded="pill"
              class="text-none hover-secondary"
              prepend-icon="mdi-open-in-new"
              @click.stop="goToNote"
            >
              Open in New Page
            </v-btn>
          </div>
        </div>
        <div v-if="thumbnail" class="note-embed-thumb flex-shrink-0">
          <v-img :src="thumbnail" cover width="160" height="auto" min-height="120" class="h-100"></v-img>
        </div>
      </div>
    </v-card>

    <v-card 
      v-else-if="note && isExpanded" 
      variant="outlined" 
      class="note-expanded-card pa-6 border-primary bg-black-05 rounded-xl"
    >
      <div class="d-flex align-center justify-space-between mb-6">
        <div class="d-flex align-center">
          <v-icon icon="mdi-book-open-variant" color="primary" class="mr-2"></v-icon>
          <span class="text-overline font-weight-black">Reading Note</span>
        </div>
        <v-btn icon="mdi-chevron-up" variant="text" size="small" @click.stop="isExpanded = false" title="Collapse"></v-btn>
      </div>

      <h1 class="text-h3 font-weight-black text-serif mb-8" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; letter-spacing: -0.5px;">{{ note.value.title }}</h1>
      
      <div class="article-content text-serif">
        <div v-for="(block, index) in note.value.blocks" :key="index" class="mb-6">
          <div v-if="block.type === 'text'" class="story-p" v-html="formatText(block.content)"></div>
          <h1 v-else-if="block.type === 'h1'" class="story-h1 text-h5 font-weight-bold mt-6 mb-4">{{ block.content }}</h1>
          <h2 v-else-if="block.type === 'h2'" class="story-h2 text-h6 font-weight-bold mt-4 mb-2">{{ block.content }}</h2>
          <h3 v-else-if="block.type === 'h3'" class="story-h3 text-subtitle-1 font-weight-bold mt-3">{{ block.content }}</h3>
          <blockquote v-else-if="block.type === 'quote'" class="story-quote pl-4 border-l-4 border-primary italic py-2 bg-white-05 rounded">
            <p>{{ block.content }}</p>
          </blockquote>
          <div v-else-if="block.type === 'spacer'" class="story-spacer text-center py-4">
            <v-icon icon="mdi-dots-horizontal" color="grey"></v-icon>
          </div>
          <div v-else-if="block.type === 'code'" class="story-code bg-black rounded-lg pa-4 my-4">
             <div v-if="block.language" class="text-caption text-grey mb-1 text-uppercase font-weight-bold">{{ block.language }}</div>
             <pre class="overflow-x-auto"><code class="text-body-2">{{ block.content }}</code></pre>
          </div>
          <div v-else-if="block.type === 'image'" class="story-image my-6">
            <v-img :src="resolveBlockImageUrl(block)" class="rounded-lg shadow-lg" max-height="600" cover></v-img>
            <div v-if="block.caption" class="text-caption text-center text-grey mt-2 italic">{{ block.caption }}</div>
          </div>
        </div>
      </div>

      <v-divider class="my-6 border-white-05"></v-divider>
      
      <div class="d-flex align-center justify-space-between">
        <v-btn
          variant="text"
          prepend-icon="mdi-chevron-up"
          color="primary"
          class="text-none font-weight-bold"
          @click.stop="isExpanded = false"
        >
          Collapse
        </v-btn>
        <v-btn
          variant="text"
          append-icon="mdi-open-in-new"
          color="grey"
          class="text-none"
          size="small"
          @click.stop="goToNote"
        >
          Open in New Page
        </v-btn>
      </div>
    </v-card>

    <v-skeleton-loader
      v-else-if="loading"
      type="list-item-three-line"
      class="my-4 rounded-xl bg-black-05 border border-white-05"
    ></v-skeleton-loader>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { atproto } from '@/services/atproto';
import { getOtherProfile } from '@/services/bluesky';

const props = defineProps<{
  handle: string;
  rkey: string;
}>();

const router = useRouter();
const note = ref<any>(null);
const loading = ref(true);
const isExpanded = ref(false);
const thumbnail = ref<string | null>(null);
const resolvedDid = ref<string>('');

const snippet = computed(() => {
  if (!note.value) return '';
  const firstTextBlock = note.value.value.blocks.find((b: any) => b.type === 'text');
  if (!firstTextBlock) return '';
  
  const text = firstTextBlock.content
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/<[^>]*>/g, '')
    .substring(0, 120);
    
  return text + (firstTextBlock.content.length > 120 ? '...' : '');
});

const formatText = (text: string) => {
  if (!text) return '';
  return text
    .replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([\s\S]*?)\*/g, '<em>$1</em>')
    .replace(/__([\s\S]*?)__/g, '<u>$1</u>')
    .replace(/~~([\s\S]*?)~~/g, '<del>$1</del>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="text-primary font-weight-bold">$1</a>')
    .replace(/#(\w+)/g, '<a href="https://bsky.app/search?q=%23$1" target="_blank" class="hashtag text-primary">#$1</a>')
    .replace(/\n/g, '<br>');
};

const resolveBlockImageUrl = (block: any) => {
  if (block.blob) {
    const cid = block.blob.ref?.$link || block.blob.ref || block.blob.cid;
    if (cid && resolvedDid.value) {
      return `https://cdn.bsky.app/img/feed_fullsize/plain/${resolvedDid.value}/${cid}@jpeg`;
    }
  }
  return block.content && block.content.startsWith('http') ? block.content : '';
};

const fetchNote = async () => {
  loading.value = true;
  try {
    // 1. Resolve handle to DID if it's not already one
    let did = props.handle;
    if (!did.startsWith('did:')) {
      const profile = await getOtherProfile(props.handle);
      if (profile) did = profile.did;
    }
    resolvedDid.value = did;
    
    // 2. Fetch the note using atproto service
    const uri = `at://${did}/id.gamedev.notes/${props.rkey}`;
    const data = await atproto.getNote(uri);
    
    if (data) {
      note.value = data;
      
      // 3. Find thumbnail
      const firstImageBlock = data.value.blocks.find((b: any) => b.type === 'image');
      if (firstImageBlock) {
        thumbnail.value = resolveBlockImageUrl(firstImageBlock);
      }
    }
  } catch (error) {
    console.error('Failed to fetch embedded note:', error);
  } finally {
    loading.value = false;
  }
};

const goToNote = () => {
  router.push(`/profile/${props.handle}/note/${props.rkey}`);
};

onMounted(fetchNote);
</script>

<script lang="ts">
// Helper to extract note info from text
export const extractNoteLinks = (text: string) => {
  if (!text) return [];
  const results = [];
  
  // Pattern 1: App internal URLs
  // Matches /profile/username/note/rkey
  const urlRegex = /\/profile\/([^\/\s]+)\/note\/([^\/\s\?#]+)/g;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    results.push({ handle: match[1], rkey: match[2] });
  }
  
  // Pattern 2: at:// URIs (unlikely in post text but good for robustness)
  const atRegex = /at:\/\/([^\/\s]+)\/id\.gamedev\.notes\/([^\/\s]+)/g;
  while ((match = atRegex.exec(text)) !== null) {
      results.push({ handle: match[1], rkey: match[2] });
  }

  // Deduplicate by rkey
  return results.filter((v, i, a) => a.findIndex(t => t.rkey === v.rkey) === i);
};
</script>

<style scoped>
.note-embed-wrapper {
  max-width: 600px;
}

.note-embed-card {
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.note-embed-card:hover {
  background: rgba(var(--v-theme-primary), 0.05) !important;
  border-color: rgba(var(--v-theme-primary), 0.3) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.note-expanded-card {
  animation: peekIn 0.3s ease-out;
}

@keyframes peekIn {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}

.text-serif {
  font-family: 'Lora', 'Georgia', serif !important;
}

.article-content {
  line-height: 1.8;
  font-size: 1.1rem;
  color: #e0e0e0;
}

.story-p {
  margin-bottom: 1.5rem;
}

.story-quote {
  font-size: 1.25rem;
  color: #fff;
  margin: 2rem 0;
  border-left-color: rgb(var(--v-theme-primary)) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.border-primary {
  border-color: rgba(var(--v-theme-primary), 0.5) !important;
}

.note-embed-thumb {
  width: 160px;
  background: rgba(0, 0, 0, 0.2);
}

@media (max-width: 600px) {
  .note-embed-thumb {
    width: 100%;
    height: 120px;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hover-secondary:hover {
  color: rgb(var(--v-theme-primary)) !important;
  background: rgba(var(--v-theme-primary), 0.1) !important;
}

.bg-black-05 {
  background: rgba(0, 0, 0, 0.2) !important;
}

.bg-white-05 {
  background: rgba(255, 255, 255, 0.05) !important;
}

:deep(.hashtag) {
  color: rgb(var(--v-theme-primary)) !important;
  text-decoration: none;
  font-weight: 600;
}

:deep(.hashtag:hover) {
  text-decoration: underline !important;
}
</style>
